/**
 * Product add-ons.
 *
 * Turns the ticked rows into hidden `items[n][id]` / `items[n][quantity]`
 * inputs. They carry a `form` attribute pointing at the product form, so they
 * are part of that form's FormData without living inside it - the same trick
 * the theme already uses for custom product option inputs.
 *
 * /cart/add.js accepts a single `id` together with an `items` array in one
 * form post, so the main product still goes through untouched and the theme's
 * own ProductForm keeps handling the response, the cart drawer and the icon.
 */
if (!customElements.get('product-addons')) {
  class ProductAddons extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.product-addons__list');
      this.inputsHolder = this.querySelector('.product-addons__inputs');
      this.summaryBtn = this.querySelector('.product-addons__summary');
      this.summaryBody = this.querySelector('[data-addons-summary]');
      this.emptySummary = this.summaryBody ? this.summaryBody.innerHTML : '';

      this.addEventListener('change', this.handleChange.bind(this));

      // A radio cannot be unticked by clicking it again, which would leave a
      // preselected extra impossible to refuse. Undo the tick instead.
      if (this.dataset.selection === 'single') {
        this.lastChecked = this.querySelector('.product-addons__input:checked');
        this.addEventListener('click', this.handleRadioClick.bind(this));
        this.addEventListener('keydown', this.handleRadioKeydown.bind(this));
      }

      if (this.summaryBtn) {
        this.summaryBtn.addEventListener('click', this.toggle.bind(this));
      }

      // A successful add empties the cart of intent: the extras went in, so the
      // rows should not stay ticked and get added twice on the next press.
      this.cartAddHandler = this.reset.bind(this);
      document.addEventListener('on:cart:add', this.cartAddHandler);

      this.updateSummary();
      ProductAddons.rebuild(this.dataset.formId);
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:add', this.cartAddHandler);
    }

    get inputs() {
      return Array.from(this.querySelectorAll('.product-addons__input'));
    }

    get selected() {
      return this.inputs.filter((input) => input.checked && !input.disabled);
    }

    handleChange() {
      this.updateSummary();
      ProductAddons.rebuild(this.dataset.formId);
    }

    /**
     * Lets a second click on the chosen row clear the choice.
     * @param {object} evt - Event object.
     */
    handleRadioClick(evt) {
      const input = evt.target.closest('.product-addons__input');
      if (!input || input.disabled) return;

      if (this.lastChecked === input) {
        input.checked = false;
        this.lastChecked = null;
        this.handleChange();
      } else {
        this.lastChecked = input;
      }
    }

    /**
     * Space on the chosen row clears it too, since a radio ignores the key
     * once it is already ticked.
     * @param {object} evt - Event object.
     */
    handleRadioKeydown(evt) {
      if (evt.key !== ' ' && evt.key !== 'Spacebar') return;
      const input = evt.target.closest('.product-addons__input');
      if (!input || input.disabled || !input.checked) return;

      evt.preventDefault();
      input.checked = false;
      this.lastChecked = null;
      this.handleChange();
    }

    toggle() {
      const open = this.summaryBtn.getAttribute('aria-expanded') === 'true';
      this.summaryBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
      this.list.hidden = open;
    }

    /**
     * Mirrors the current selection into the collapsed summary row.
     */
    updateSummary() {
      if (!this.summaryBody) return;
      const chosen = this.selected;

      if (chosen.length === 0) {
        this.summaryBody.innerHTML = this.emptySummary;
        return;
      }

      // Preview the first choice, and say how many more came with it, rather
      // than replacing the row with a bare count
      const row = chosen[0].closest('.product-addons__row');
      const media = row.querySelector('.product-addons__media');
      const body = row.querySelector('.product-addons__body');
      let html = (media ? media.outerHTML : '') + (body ? body.outerHTML : '');

      if (chosen.length > 1) {
        // [count] is the whole selection, [more] is what the preview leaves out
        const template = this.dataset.summaryMany || '[count]';
        const label = template
          .replace('[count]', chosen.length)
          .replace('[more]', chosen.length - 1);
        html += `<span class="product-addons__label product-addons__more product-label">${label}</span>`;
      }

      this.summaryBody.innerHTML = html;
    }

    reset() {
      this.inputs.forEach((input) => { input.checked = false; });
      this.lastChecked = null;
      this.updateSummary();
      ProductAddons.rebuild(this.dataset.formId);
    }

    /**
     * Rewrites the hidden inputs for every add-ons block that submits with the
     * same form, so the `items` indices stay contiguous and unique across them.
     * @param {string} formId - id of the product form.
     */
    static rebuild(formId) {
      const blocks = Array.from(
        document.querySelectorAll(`product-addons[data-form-id="${CSS.escape(formId)}"]`)
      );
      let index = 0;

      blocks.forEach((addons) => {
        const holder = addons.querySelector('.product-addons__inputs');
        if (!holder) return;
        holder.innerHTML = '';

        addons.selected.forEach((input) => {
          const quantity = parseInt(input.dataset.addonQuantity, 10) || 1;
          holder.appendChild(ProductAddons.hiddenInput(`items[${index}][id]`, input.value, formId));
          holder.appendChild(ProductAddons.hiddenInput(`items[${index}][quantity]`, quantity, formId));
          index += 1;
        });
      });
    }

    /**
     * @param {string} name - Input name.
     * @param {string|number} value - Input value.
     * @param {string} formId - id of the form the input submits with.
     * @returns {HTMLInputElement}
     */
    static hiddenInput(name, value, formId) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      if (formId) input.setAttribute('form', formId);
      return input;
    }
  }

  customElements.define('product-addons', ProductAddons);
}
